import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Loader2, Download } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  setCfcData,
  setCurrentTab,
  setUserInitiatedNav,
} from "../../../store/slices/cfcSlice";
import api from "@/utils/api";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

const sanctionSchema = z.object({
  is_user_accept: z.boolean().refine((val) => val === true, {
    message: "You must agree to continue",
  }),
});

const SanctionDetails = ({ data, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const nav = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(sanctionSchema),
    defaultValues: {
      is_user_accept: data?.sanction?.is_user_accept || false,
    },
  });

  const onSubmit = async (formData) => {
    if (!formData.is_user_accept) return;

    setIsSubmitting(true);
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== undefined && formData[key] !== null) {
          if (formData[key] instanceof File) {
            submitData.append(key, formData[key]);
          } else {
            submitData.append(key, formData[key]);
          }
        }
      });
      submitData.append("id", data?.id || "");

      const response = await api.post(
        "/cfc-subsidies/user_sanction_acceptance",
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      dispatch(setCfcData(response?.data?.data || {}));

      toast({
        title: "Success!",
        description: "Application Saved successfully",
        variant: "success",
      });

      dispatch(setUserInitiatedNav(true));
      nav("/");
    } catch (err) {
      toast({
        title: "Submission failed",
        description: err.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!data?.sanction) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Sanction Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500">No sanction details available</p>
        </CardContent>
      </Card>
    );
  }

  const { sanction } = data;
  const isAlreadyAccepted = sanction.is_user_accept === true;

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Sanction Details
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6 mt-5">
          {/* Sanction Information */}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Provisional Sanction By District Officer</Label>
              <Input
                value={sanction.district_officer?.name || "N/A"}
                disabled
                className="bg-gray-50"
              />
            </div>

            <div>
              <Label>Provisional Sanction Order Number</Label>
              <Input
                value={sanction.sanction_order_no}
                disabled
                className="bg-gray-50"
              />
            </div>

            <div>
              <Label>Provisional Sanction Order Date</Label>
              <Input
                value={new Date(
                  sanction.sanction_order_date
                ).toLocaleDateString("en-IN")}
                disabled
                className="bg-gray-50"
              />
            </div>

            <div>
              <Label>Provisional Sanction Amount</Label>
              <Input
                value={`₹${Number(
                  sanction.sanction_order_amount
                ).toLocaleString("en-IN")}`}
                disabled
                className="bg-gray-50"
              />
            </div>

            {sanction.description && (
              <div className="md:col-span-2">
                <Label>Provisional Description</Label>
                <Input
                  value={sanction.description}
                  disabled
                  className="bg-gray-50"
                />
              </div>
            )}

            {sanction.sanctioned_copy && (
              <div className="md:col-span-2">
                <Label>Sanctioned Copy</Label>
                <div className="mt-2">
                  <a
                    href={sanction.sanctioned_copy}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Sanction Copy
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Already Accepted Status */}
          {isAlreadyAccepted && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-md">
              <p className="text-green-800 font-medium">
                ✓ You have already accepted these sanction details
              </p>
              {sanction.user_accept_date && (
                <p className="text-green-600 text-sm mt-1">
                  Accepted on:{" "}
                  {new Date(sanction.user_accept_date).toLocaleDateString(
                    "en-IN"
                  )}
                </p>
              )}
            </div>
          )}

          {/* Agreement Checkbox - Only show if not already accepted */}
          {!isAlreadyAccepted && (
            <>
              <div className="flex items-start space-x-2 mt-6 p-4 border rounded-md bg-blue-50">
                <Checkbox
                  id="is_user_accept"
                  {...register("is_user_accept")}
                  checked={watch("is_user_accept")}
                  onCheckedChange={(val) => setValue("is_user_accept", val)}
                />
                <Label
                  htmlFor="is_user_accept"
                  className="text-sm leading-tight cursor-pointer"
                >
                  I have reviewed the above sanction details and agree to
                  proceed with the application
                </Label>
              </div>
              {errors.is_user_accept && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.is_user_accept.message}
                </p>
              )}

              {/* Submit Button */}
              <div className="flex justify-end mt-6 gap-2">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="min-w-[150px]"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    "Agree and Submit"
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </form>
  );
};

export default SanctionDetails;
